/**
 * Partite della squadra-stagione (V16), a calendario mensile o in elenco.
 *
 * Si apre sull'elenco, che mette in testa le partite sul campo, poi
 * le programmate rimaste indietro, le prossime dalla piu vicina e le giocate
 * dalla piu recente, ogni gruppo nella sua card e cinque righe per pagina;
 * prossime e giocate si affiancano solo se ci sono tutte e due.
 * I filtri per stato e competizione valgono per entrambe le viste; il mese
 * no: l'elenco si apre su tutti i mesi e il calendario sul mese corrente.
 * Vista, mese, giorno scelto e pagine stanno nell'URL. La testata riassume la
 * stagione. Ogni riga apre la pagina unica della partita, che assume da sola
 * la forma giusta per lo stato. Mister e Dirigente creano da qui la nuova
 * partita (V17), in un modale.
 */

import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CalendarClockIcon,
  CircleAlertIcon,
  createLucideIcon,
  HistoryIcon,
  type LucideIcon,
  RadioIcon,
  TrophyIcon,
} from 'lucide-react';
import { soccerBall } from '@lucide/lab';
import { dataLocale, puo } from '@calcio/shared';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { cn } from '@/lib/utils';
import { PulsanteNuovo } from '../comuni/Azioni.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import {
  Filtri,
  Filtro,
  GruppoPaginato,
  Riepilogo,
  TUTTI,
  VoceRiepilogo,
} from '../comuni/FiltriVista.js';
import { useDati } from '../comuni/dati.js';
import {
  type GiornoProposto,
  meseDi,
  meseIniziale,
  mesiDellaStagione,
  nomeMese,
  stagioneCon,
} from '../comuni/calendario.js';
import { useContesto } from '../navigazione/contesto.js';
import { CalendarioPartite } from './CalendarioPartite.js';
import { DialogPartita } from './DialogPartita.js';
import { ETICHETTA_STATO_PARTITA } from './partita.js';
import { type PartitaInElenco, raggruppa, riepilogoStagione, segnoDi } from './raggruppa.js';
import { RigaPartita } from './RigaPartita.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

/** Il pallone degli eventi di gol, per i gol fatti e subiti del riepilogo. */
const SoccerBall = createLucideIcon('soccer-ball', soccerBall);

/** Le chiavi della pagina di ogni gruppo nell'URL: un filtro nuovo le azzera. */
const PAGINE = {
  inCorso: 'sul-campo',
  daChiudere: 'da-chiudere',
  prossime: 'prossime',
  giocate: 'giocate',
} as const;
const SENZA_PAGINE = Object.fromEntries(Object.values(PAGINE).map((k) => [k, null]));

export function ElencoPartite() {
  const { ss } = useParams<{ ss: string }>();
  // Il componente si rimonta cambiando squadra: i filtri della precedente
  // non hanno senso sull'altra, una competizione puo non esistere li.
  return <Elenco key={ss} ss={ss!} />;
}

function Elenco({ ss }: { ss: string }) {
  const { utente, squadre, erroreSquadre } = useContesto();
  const { dati, errore } = useDati<PartitaInElenco[]>(`/squadre-stagione/${ss}/partite`);
  const [params, setParams] = useSearchParams();
  const [stato, setStato] = useState<string>(TUTTI);
  const [competizione, setCompetizione] = useState<string>(TUTTI);
  /** Il modale della nuova partita: aperta, con il giorno scelto nel calendario se c'e. */
  const [nuova, setNuova] = useState<{ giorno?: GiornoProposto } | null>(null);
  const calendario = params.get('vista') === 'calendario';
  const mese = params.get('mese') ?? TUTTI;
  const stagione = squadre?.find((s) => s.id === ss);
  const gestisce = puo(utente.assegnazioni, 'partita.gestisci', ss);
  const adesso = new Date();

  const testata = (
    <Testata
      titolo="Partite"
      voce="partite"
      accanto={dati && dati.length > 0 && <RiepilogoPartite elenco={dati} adesso={adesso} />}
      azione={gestisce && <PulsanteNuovo etichetta="Nuova partita" onClick={() => setNuova({})} />}
    />
  );

  const modale = gestisce && (
    <DialogPartita
      ss={ss}
      aperto={nuova !== null}
      giorno={nuova?.giorno}
      onChiudi={() => setNuova(null)}
    />
  );

  // Il calendario si ferma ai mesi della stagione, che arrivano col contesto.
  if (!dati || (calendario && !stagione)) {
    return (
      <div>
        {testata}
        {modale}
        <Corpo>
          <StatoCaricamento errore={errore ?? (calendario ? erroreSquadre : null)} />
        </Corpo>
      </div>
    );
  }

  if (dati.length === 0) {
    return (
      <div>
        {testata}
        {modale}
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nessuna partita</EmptyTitle>
            <EmptyDescription>
              {gestisce
                ? 'Aggiungi la prima partita: data, avversario e competizione; i convocati si scelgono dopo.'
                : 'Mister e Dirigente non hanno ancora messo in calendario partite.'}
            </EmptyDescription>
          </EmptyHeader>
          {gestisce && (
            <EmptyContent>
              <PulsanteNuovo etichetta="Nuova partita" onClick={() => setNuova({})} />
            </EmptyContent>
          )}
        </Empty>
      </div>
    );
  }

  /** Cambia i parametri nell'URL senza allungare la cronologia a ogni tocco. */
  const aggiorna = (valori: Record<string, string | null>) => {
    const q = new URLSearchParams(params);
    for (const [chiave, valore] of Object.entries(valori)) {
      if (valore === null || valore === TUTTI) q.delete(chiave);
      else q.set(chiave, valore);
    }
    setParams(q, { replace: true });
  };

  const oggi = dataLocale(adesso);
  const perFiltri = dati.filter(
    (p) =>
      (stato === TUTTI || p.stato === stato) &&
      (competizione === TUTTI || p.competizione === competizione),
  );
  const competizioni = [...new Set(dati.map((p) => p.competizione))].sort();

  const cambiaVista = (vista: 'calendario' | 'elenco') => {
    // Il mese non passa da una vista all'altra: il calendario riparte dal
    // mese corrente, l'elenco da tutti i mesi.
    if (vista === 'calendario') aggiorna({ vista, mese: null, ...SENZA_PAGINE });
    else aggiorna({ vista: null, mese: null, giorno: null });
  };

  const filtri = (
    <>
      <Filtro
        etichetta="Stato"
        valore={stato}
        onCambia={(s) => {
          setStato(s);
          aggiorna(SENZA_PAGINE);
        }}
        opzioni={[
          { valore: TUTTI, etichetta: 'Tutti' },
          ...Object.entries(ETICHETTA_STATO_PARTITA).map(([valore, etichetta]) => ({
            valore,
            etichetta,
          })),
        ]}
      />
      <Filtro
        etichetta="Competizione"
        valore={competizione}
        onCambia={(c) => {
          setCompetizione(c);
          aggiorna(SENZA_PAGINE);
        }}
        opzioni={[
          { valore: TUTTI, etichetta: 'Tutte' },
          ...competizioni.map((c) => ({ valore: c, etichetta: c })),
        ]}
      />
    </>
  );

  if (calendario && stagione) {
    // Una partita non e legata alle date della stagione: i suoi mesi ci sono comunque.
    const estesa = stagioneCon(stagione, dati);
    const meseCalendario = meseIniziale(estesa, adesso, mese);
    return (
      <div>
        {testata}
        <Corpo className="flex flex-col gap-4">
          <Filtri calendario onVista={cambiaVista}>
            {filtri}
          </Filtri>
          <CalendarioPartite
            ss={ss}
            mesi={mesiDellaStagione(estesa)}
            tutte={dati}
            filtrate={perFiltri}
            mese={meseCalendario}
            giorno={params.get('giorno')}
            gestisce={gestisce}
            adesso={adesso}
            onMese={(m) => aggiorna({ mese: m, giorno: null })}
            onGiorno={(g) => aggiorna({ mese: meseCalendario, giorno: g })}
            onNuova={(giorno) => setNuova({ giorno })}
          />
          {modale}
        </Corpo>
      </div>
    );
  }

  // Un mese arrivato dal calendario puo non avere partite: resta tra le voci.
  const mesi = [
    ...new Set([...dati.map((p) => meseDi(p.dataOra)), ...(mese === TUTTI ? [] : [mese])]),
  ].sort();
  const filtrate = perFiltri.filter((p) => mese === TUTTI || meseDi(p.dataOra) === mese);
  const g = raggruppa(filtrate, adesso);

  return (
    <div>
      {testata}
      {modale}
      <Corpo className="flex flex-col gap-4">
        <Filtri calendario={false} onVista={cambiaVista}>
          {filtri}
          <Filtro
            etichetta="Mese"
            valore={mese}
            onCambia={(m) => aggiorna({ mese: m, ...SENZA_PAGINE })}
            opzioni={[
              { valore: TUTTI, etichetta: 'Tutti' },
              ...mesi.map((m) => ({ valore: m, etichetta: nomeMese(m) })),
            ]}
          />
        </Filtri>

        {filtrate.length === 0 && <StatoVuoto titolo="Nessuna partita con questi filtri" />}

        <GruppoPartite
          titolo="Sul campo"
          Icona={RadioIcon}
          colore="text-stato-infortunato"
          partite={g.inCorso}
          chiave={PAGINE.inCorso}
          oggi={oggi}
          ss={ss}
        />
        <GruppoPartite
          titolo="Da chiudere"
          Icona={CircleAlertIcon}
          colore="text-stato-recupero"
          nota="Programmate con la data già passata: vanno concluse o annullate, altrimenti falsano le statistiche di convocazione."
          partite={g.daChiudere}
          chiave={PAGINE.daChiudere}
          oggi={oggi}
          ss={ss}
        />
        <div
          className={cn(
            'grid gap-4',
            g.prossime.length > 0 && g.giocate.length > 0 && '@5xl:grid-cols-2',
          )}
        >
          <GruppoPartite
            titolo="Prossime"
            Icona={CalendarClockIcon}
            partite={g.prossime}
            chiave={PAGINE.prossime}
            oggi={oggi}
            ss={ss}
          />
          <GruppoPartite
            titolo="Giocate"
            Icona={HistoryIcon}
            partite={g.giocate}
            chiave={PAGINE.giocate}
            oggi={oggi}
            ss={ss}
          />
        </div>
      </Corpo>
    </div>
  );
}

/** I numeri della stagione accanto al titolo: esiti e gol delle concluse, poi quelle da giocare. */
function RiepilogoPartite({ elenco, adesso }: { elenco: PartitaInElenco[]; adesso: Date }) {
  const r = riepilogoStagione(elenco, adesso);
  const giocate = r.vinte + r.pareggiate + r.perse;
  return (
    <Riepilogo>
      {giocate > 0 && (
        <>
          <VoceRiepilogo
            Icona={TrophyIcon}
            valore={
              <>
                <span className="text-stato-disponibile">{r.vinte}</span>-{r.pareggiate}-
                <span className="text-stato-infortunato">{r.perse}</span>
              </>
            }
            titolo="Vinte, pareggiate e perse"
            colore="text-stato-disponibile"
          />
          <VoceRiepilogo
            Icona={SoccerBall}
            valore={
              <>
                <span className="text-stato-disponibile">{r.golFatti}</span>:
                <span className="text-stato-infortunato">{r.golSubiti}</span>
              </>
            }
            titolo="Gol fatti e subiti"
          />
        </>
      )}
      <VoceRiepilogo Icona={CalendarClockIcon} valore={r.inProgramma} titolo="Da giocare" />
      {r.daChiudere > 0 && (
        <VoceRiepilogo
          Icona={CircleAlertIcon}
          valore={r.daChiudere}
          titolo="Da chiudere"
          colore="text-stato-recupero"
        />
      )}
    </Riepilogo>
  );
}

function GruppoPartite({
  partite,
  oggi,
  ss,
  ...gruppo
}: {
  titolo: string;
  Icona: LucideIcon;
  colore?: string;
  nota?: string;
  partite: PartitaInElenco[];
  chiave: string;
  oggi: string;
  ss: string;
}) {
  return (
    <GruppoPaginato
      {...gruppo}
      elementi={partite}
      riga={(p) => <RigaPartita partita={p} segno={segnoDi(p, oggi)} ss={ss} />}
    />
  );
}
